import { useState, useEffect } from 'react';
import { doc, setDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import './Editor.css';

export const RegulationTextCreator = ({ onSave, onCancel, initialData, onDelete }) => {
    const isEditing = !!initialData;

    const [formData, setFormData] = useState({
        text_name: '',
        text_date: '',
        text_link: '',
        text_id: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (initialData) {
            setFormData({
                text_name: initialData.text_name || '',
                text_date: initialData.text_date || '',
                text_link: initialData.text_link || '',
                text_id: initialData.text_id || ''
            });
        } else {
            setFormData({
                text_name: '',
                text_date: '',
                text_link: '',
                text_id: Date.now() // Default to timestamp for unique ID if creating new
            });
        }
    }, [initialData]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        try {
            if (!formData.text_name) throw new Error("Le nom du texte est obligatoire.");

            // Derive document ID from name (sanitize)
            // e.g. "Décret CabSat" -> "decret-cabsat"
            const firestoreDocId = formData.text_name
                .toLowerCase()
                .normalize("NFD").replace(/[\u0300-\u036f]/g, "") // Remove accents
                .replace(/[^a-z0-9]+/g, '-') // Replace non-alphanumeric with hyphens
                .replace(/^-+|-+$/g, ''); // Trim hyphens

            if (!firestoreDocId) throw new Error("Impossible de générer un ID valide à partir du nom.");

            const finalText = {
                ...formData,
                text_id: Number(formData.text_id) // Ensure numerical ID
            };

            // Save to Firestore using the sanitized name as Doc ID
            await setDoc(doc(db, "regulation_texts", firestoreDocId), finalText, { merge: true });

            onSave({ ...finalText, firestoreId: firestoreDocId });
        } catch (error) {
            console.error("Error saving text:", error);
            alert("Erreur: " + error.message);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDelete = async () => {
        if (!initialData || !initialData.text_name) return;

        // Re-derive ID to ensure we delete the right document
        const firestoreDocId = initialData.text_name
            .toLowerCase()
            .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/^-+|-+$/g, '');

        if (window.confirm(`ATTENTION : Supprimer le texte "${formData.text_name}" ?\n\nCela peut affecter les règles qui y font référence.`)) {
            setIsSubmitting(true);
            try {
                await deleteDoc(doc(db, "regulation_texts", firestoreDocId));
                if (onDelete) onDelete(initialData.text_id);
            } catch (error) {
                console.error("Error deleting text:", error);
                alert("Erreur lors de la suppression: " + error.message);
                setIsSubmitting(false);
            }
        }
    };

    return (
        <div className="rule-editor">
            <h3 style={{ borderBottom: '2px solid #eee', paddingBottom: '10px', marginBottom: '20px' }}>
                {isEditing ? 'Modifier le Texte Réglementaire' : 'Nouveau Texte Réglementaire'}
            </h3>
            
            <form onSubmit={handleSubmit}>
                <div className="form-group">
                    <label htmlFor="txt-name">Nom du texte</label>
                    <input 
                        type="text" 
                        name="text_name" 
                        id="txt-name" 
                        value={formData.text_name} 
                        onChange={handleChange} 
                        placeholder="Ex: Décret CabSat"
                        required 
                    />
                </div>

                <div className="form-group">
                    <label htmlFor="txt-date">Date / Période</label>
                    <input 
                        type="text" 
                        name="text_date" 
                        id="txt-date" 
                        value={formData.text_date} 
                        onChange={handleChange} 
                        placeholder="Ex: décembre 2021"
                    />
                </div>

                <div className="form-group">
                    <label htmlFor="txt-link">Lien (URL)</label>
                    <input 
                        type="url" 
                        name="text_link" 
                        id="txt-link" 
                        value={formData.text_link} 
                        onChange={handleChange} 
                        placeholder="https://legifrance.gouv.fr/..."
                    />
                </div>

                <div className="form-group">
                    <label htmlFor="txt-id">ID Unique (Interne)</label>
                    <input 
                        type="number" 
                        name="text_id" 
                        id="txt-id" 
                        value={formData.text_id} 
                        onChange={handleChange} 
                        required 
                    />
                    <small style={{color: '#666'}}>Identifiant numérique utilisé pour relier les règles à ce texte.</small>
                </div>

                <div className="form-actions" style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                    <button type="submit" className="save-btn" disabled={isSubmitting}>
                        {isSubmitting ? 'Enregistrement...' : 'Enregistrer'}
                    </button>
                    <button type="button" onClick={onCancel} className="cancel-btn">
                        Fermer
                    </button>
                    
                    {isEditing && (
                        <button 
                            type="button" 
                            onClick={handleDelete} 
                            className="delete-btn" 
                            disabled={isSubmitting}
                            style={{ marginLeft: 'auto', backgroundColor: '#dc3545', color: 'white' }}
                        >
                            Supprimer
                        </button>
                    )}
                </div>
            </form>
        </div>
    );
};